import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useLaunchParams } from "@telegram-apps/sdk-react";
import {
  usePriceAlertConfigs,
  usePriceAlertStats,
  useCreatePriceAlertConfig,
  useUpdatePriceAlertConfig,
  useDeletePriceAlertConfig,
} from "@/hooks/usePriceAlerts";
import {
  PriceAlertConfigData,
  CreatePriceAlertConfigPayload,
  UpdatePriceAlertConfigPayload,
} from "@/api/price-alerts";
import { PriceAlertConfigTile } from "@/components/PriceAlertConfigTile";
import { PriceAlertConfigSheetDesktop } from "@/components/PriceAlertConfigSheet.Desktop";
import { PriceAlertStatsCards } from "@/components/PriceAlertStatsCards";
import { Loader2, ArrowLeft, Plus } from "lucide-react";
import { DesktopLayout } from "@/layouts";

export const PriceAlertConfigPageDesktop: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const launchParams = useLaunchParams();
  const userId = launchParams.initData?.user?.id?.toString() || "";

  const [showAddSheet, setShowAddSheet] = useState(false);
  const [editingConfig, setEditingConfig] = useState<PriceAlertConfigData | null>(null);

  const { data: configs, isLoading } = usePriceAlertConfigs(userId);
  const { data: statsData, isLoading: isLoadingStats } = usePriceAlertStats(userId);
  const createMutation = useCreatePriceAlertConfig(userId);
  const updateMutation = useUpdatePriceAlertConfig(userId);
  const deleteMutation = useDeletePriceAlertConfig(userId);

  useEffect(() => {
    if (searchParams.get("add") === "true") {
      setShowAddSheet(true);
    }
  }, [searchParams]);

  const handleCreate = (payload: CreatePriceAlertConfigPayload | UpdatePriceAlertConfigPayload) => {
    createMutation.mutate(payload as CreatePriceAlertConfigPayload, {
      onSuccess: () => setShowAddSheet(false),
    });
  };

  const handleUpdate = (payload: CreatePriceAlertConfigPayload | UpdatePriceAlertConfigPayload) => {
    if (!editingConfig) return;
    updateMutation.mutate(
      { configId: editingConfig.id, payload: payload as UpdatePriceAlertConfigPayload },
      { onSuccess: () => setEditingConfig(null) },
    );
  };

  const handleDelete = (configId: string) => {
    if (globalThis.confirm("Delete this alert configuration?")) {
      deleteMutation.mutate(configId);
    }
  };

  const headerActions = (
    <div className="flex items-center gap-2">
      <button
        onClick={() => navigate("/price-alerts")}
        className="flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white font-medium rounded-lg transition-all duration-150 shadow-lg"
      >
        <ArrowLeft className="w-4 h-4" />
        <span>Back to Alerts</span>
      </button>
      <button
        onClick={() => setShowAddSheet(true)}
        className="flex items-center gap-2 px-4 py-2 bg-accent hover:bg-accent/80 text-white font-medium rounded-lg transition-all duration-150 shadow-lg hover:shadow-accent/20"
      >
        <Plus className="w-4 h-4" />
        <span>Add Alert</span>
      </button>
    </div>
  );

  return (
    <>
      <DesktopLayout
        pageTitle="Manage Price Alerts"
        pageDescription="Create, edit, and manage your price alert configurations"
        showRightSidebar={false}
        headerActions={headerActions}
      >
        <div className="mb-6">
          <PriceAlertStatsCards stats={statsData} isLoading={isLoadingStats} />
        </div>

        {isLoading && (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-accent" />
          </div>
        )}

        {!isLoading && (!configs || configs.length === 0) && (
          <div className="text-center py-12 bg-tertiary border border-border rounded-xl">
            <p className="text-text-secondary">No alert configurations yet</p>
            <p className="text-text-secondary text-sm mt-1">
              Click &quot;Add Alert&quot; to create your first price alert
            </p>
          </div>
        )}

        {!isLoading && configs && configs.length > 0 && (
          <div
            className="grid gap-4 justify-center"
            style={{
              gridTemplateColumns: "repeat(auto-fill, minmax(400px, 480px))",
            }}
          >
            {configs.map((config) => (
              <div key={config.id} className="w-full max-w-[480px]">
                <PriceAlertConfigTile
                  config={config}
                  onEdit={(c) => setEditingConfig(c)}
                  onDelete={handleDelete}
                />
              </div>
            ))}
          </div>
        )}
      </DesktopLayout>

      {/* Add Sheet */}
      <PriceAlertConfigSheetDesktop
        open={showAddSheet}
        onOpenChange={setShowAddSheet}
        onSubmit={handleCreate}
        isLoading={createMutation.isPending}
      />

      {/* Edit Sheet */}
      <PriceAlertConfigSheetDesktop
        open={!!editingConfig}
        onOpenChange={(open) => { if (!open) setEditingConfig(null); }}
        config={editingConfig}
        onSubmit={handleUpdate}
        isLoading={updateMutation.isPending}
      />
    </>
  );
};
